import { RefObject, useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../stores/hooks/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLoader,
  faStars,
  faRotateRight,
} from "@fortawesome/pro-light-svg-icons";
import { faDownload } from "@fortawesome/pro-solid-svg-icons";
import { pollForBriefGeneration } from "../../../helpers/services/pollingService";
import { downloadCreatorBrief } from "../../../stores/thunks/creatorBriefsThunk";
import { CreatorBrief } from "../../../stores/types/creatorBriefsTypes";
import {
  RevAlert,
  RevButton,
  RevCard,
  RevStatusBadge,
} from "@revmatics/catalyst";
import { setBriefStatuses } from "../../../stores/slice/onboardingSlice.tsx";

type Props = {
  pollingControllers: RefObject<Map<number, AbortController>>;
};

export const Step4_GenerateBrief = ({ pollingControllers }: Props) => {
  const dispatch = useAppDispatch();

  const { briefsForGenerate, briefStatuses } = useAppSelector(
    (state) => state.onboarding,
  );

  const briefStatusesRef = useRef(briefStatuses);
  useEffect(() => {
    briefStatusesRef.current = briefStatuses;
  }, [briefStatuses]);

  useEffect(() => {
    const controllers = pollingControllers.current;

    controllers.forEach((controller, id) => {
      const existsInBriefs = briefsForGenerate.some((b) => b.id === id);
      if (!existsInBriefs) {
        controller.abort();
        controllers.delete(id);
      }
    });

    briefsForGenerate.forEach((brief) => {
      const status = briefStatusesRef.current[brief.id];

      if (status === "complete" || status === "failed") return;
      if (controllers.has(brief.id)) return;

      if (status !== "generating") {
        dispatch(setBriefStatuses({ id: brief.id, status: "generating" }));
      }

      const controller = new AbortController();
      controllers.set(brief.id, controller);

      pollForBriefGeneration(brief.id, { signal: controller.signal })
        .then(() => {
          dispatch(setBriefStatuses({ id: brief.id, status: "complete" }));
        })
        .catch((err) => {
          if (err.name !== "AbortError") {
            console.error(`Polling failed for brief ${brief.id}:`, err);
            dispatch(setBriefStatuses({ id: brief.id, status: "failed" }));
          }
        })
        .finally(() => {
          controllers.delete(brief.id);
        });
    });

    return () => {
      controllers.forEach((controller, id) => {
        const status = briefStatusesRef.current[id];
        if (status !== "generating") {
          controller.abort();
          controllers.delete(id);
        }
      });
    };
  }, [briefsForGenerate, dispatch, pollingControllers]);

  const handleDownload = (brief: CreatorBrief) => {
    dispatch(
      downloadCreatorBrief({
        id: String(brief.id),
        name: brief.title,
        params: { format: "pdf" },
      }),
    );
  };

  const handleRetry = (brief: CreatorBrief | undefined) => {
    if (!brief) return;
    dispatch(setBriefStatuses({ id: brief.id, status: "generating" }));
    const controller = new AbortController();
    pollingControllers.current.set(brief.id, controller);

    pollForBriefGeneration(brief.id, { signal: controller.signal })
      .then(() => {
        dispatch(setBriefStatuses({ id: brief.id, status: "complete" }));
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          console.error(`Retry failed for brief ${brief.id}:`, err);
          dispatch(setBriefStatuses({ id: brief.id, status: "failed" }));
        }
      })
      .finally(() => {
        pollingControllers.current.delete(brief.id);
      });
  };

  return (
    <div>
      <RevCard
        title="Creator Brief(s)"
        text="We're going to generate a personalized creator brief just for you based on the information you provide during this onboarding process."
        textSize="typography-body-sm"
        className="!px-4 !pb-4"
      >
        <RevAlert
          className="mb-3"
          isDismissible={false}
          data={{
            type: "info",
            id: "creator-brief-generating-alert",
            show: true,
            heading: "Generation of Creator Brief in Progress",
            text: "It can take up to 90 seconds to generate the brief.",
          }}
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-3">
          {briefsForGenerate.map((brief) => (
            <div
              key={"generation-landing-page" + brief.id}
              className="bg-white border border-monochrome-200 rounded-lg p-6 flex flex-col gap-4 min-h-[168px]"
            >
              <div className="flex items-center">
                <h6 className="typography-body-bold-sm truncate">
                  {brief.title}
                </h6>
                {brief?.is_ai_generated && (
                  <FontAwesomeIcon
                    icon={faStars}
                    className="typography-body-md text-purple-600 ml-2 flex-shrink-0"
                  />
                )}
              </div>

              <div className="flex-grow flex flex-col justify-between gap-2">
                {briefStatuses[brief.id] === "complete" ? (
                  <>
                    <RevButton
                      text="Download Creator Brief"
                      iconLeft={faDownload}
                      variant="primary"
                      handleClick={() => handleDownload(brief)}
                      className="w-fit"
                    />
                    <div className="flex items-start gap-2 mt-2 flex-wrap">
                      <span className="typography-body-sm text-monochrome-500 whitespace-nowrap">
                        Preview Creator Brief
                      </span>
                      <RevStatusBadge
                        text="Coming Soon"
                        color="info"
                        className="whitespace-nowrap"
                      />
                    </div>
                  </>
                ) : briefStatuses[brief.id] === "failed" ? (
                  <div className="text-center">
                    <p className="typography-body-sm text-red-500 mb-2">
                      Generation failed.
                    </p>
                    <RevButton
                      text="Retry"
                      variant="secondary"
                      iconLeft={faRotateRight}
                      handleClick={() => handleRetry(brief)}
                    />
                  </div>
                ) : (
                  <>
                    <FontAwesomeIcon
                      icon={faLoader}
                      className="text-primary-500 text-[36px] animate-spin"
                    />
                    <h6>Generating...</h6>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </RevCard>
    </div>
  );
};
